import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useUltraCriticalAlarms } from '../../hooks/useUltraCriticalAlarms';
import { formatDateTime } from '../../helpers/formatDateTime';
import { getAlarmSections, alarmSectionsToText, alarmSectionsToHtml } from '../../helpers/formatAlarmClipboard';
import type { BureauAlarm } from '../../services/bureauManagement/bureauManagement.types';
import { localAcknowledge } from '../../services/bureauManagement/bureauManagement.service';
import { getProfile } from '../../services/userManagement/userManagement.service';
import { getRecipient, listRecipients } from '../../services/distribution/distribution.service';
import { alarmAcknowledgedEvent } from '../../services/events/alarmEvents';
import { useAuthContext } from '../../contexts/useAuthContext';
import { TOAST_ERROR_MS, TOAST_LONG_MS, TOAST_SUCCESS_MS } from '../../constants/timeouts';
import { ActionList, StatusMessage } from '../ui';
import LoadingSpinner from '../ui/LoadingSpinner';
import Modal from '../ui/Modal';
import { ModalContent } from '../ui/ModalContent';
import BulkActionToolbar from '../ui/BullActionToolbar';
import BureauAcknowledgeForm from '../forms/BureauAcknowledgeForm';

const UNKNOWN_SITE = 'Unknown Site';
const UNKNOWN_VALUE = '--';
const DEFAULT_ALARM_STATE = 'ACTIVE - UNACKNOWLEDGED';

function getAlarmId(alarm: BureauAlarm): number | null {
  if (typeof alarm.bureauAlarmId === 'number') {
    return alarm.bureauAlarmId;
  }
  if (typeof alarm.id === 'number') {
    return alarm.id;
  }
  return null;
}

function getVariant<T,>(obj: unknown, keys: string[]): T | undefined {
  if (!obj || typeof obj !== 'object') {
    return undefined;
  }

  const record = obj as Record<string, unknown>;
  for (const key of keys) {
    const value = record[key];
    if (value !== undefined && value !== null) {
      return value as T;
    }
  }

  return undefined;
}

function formatRecipientName(value: string | undefined | null): string {
  if (!value || value === '—' || value === 'Unknown') {
    return value || 'Unknown';
  }

  if (/^(Bureau\s*)+$/i.test(value.trim())) {
    return 'Connected Services Hub';
  }

  return value;
}

function getAlarmStateText(alarm: BureauAlarm): string {
  if (alarm.sourceAlarmState?.toLowerCase() === 'active' && alarm.localAck) {
    return 'Active - Acknowledged';
  }

  if (alarm.sourceAlarmState?.toLowerCase() === 'active') {
    return 'Active - Unacknowledged';
  }

  return (
    alarm.sourceAlarmState ||
    alarm.bureauAlarmState ||
    DEFAULT_ALARM_STATE
  );
}

function getAlarmStateClassName(alarm: BureauAlarm): string {
  const sourceState = alarm.sourceAlarmState?.toLowerCase();

  if (sourceState === 'active' && alarm.localAck) {
    return 'status__completed';
  }
  if (sourceState === 'active') {
    return 'status__active';
  }
  if (sourceState === 'reset') {
    return 'status__reset';
  }
  if (sourceState === 'acknowledged') {
    return 'status__completed';
  }
  if (sourceState === 'normal') {
    return 'status__normal';
  }
  if (sourceState === 'disabled') {
    return 'status__disabled';
  }

  const bureauState = alarm.bureauAlarmState?.toLowerCase();
  if (bureauState?.includes('notactive')) {
    return 'status__inactive';
  }

  return 'status__active';
}

function getAcknowledgedByName(alarm: BureauAlarm): string | null {
  return (
    getVariant<string>(alarm, ['localAckByName', 'LocalAckByName', 'acknowledgedByName']) ||
    getVariant<string>(alarm, ['localAckBy', 'LocalAckBy', 'acknowledgedBy']) ||
    null
  );
}

const UltraCriticalAlarms: React.FC = () => {
  const { userProfile } = useAuthContext();
  const {
    alarms,
    newLast15Minutes,
    sitesAffected,
    loading,
    error,
    refresh,
  } = useUltraCriticalAlarms();
  const [isExpanded, setIsExpanded] = useState<boolean>(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [acknowledgingId, setAcknowledgingId] = useState<number | null>(null);
  const [openModalConfirm, setOpenModalConfirm] = useState<boolean>(false);
  const [modalHeadingText, setModalHeadingText] = useState<string | undefined>(undefined);
  const [modalMessageText, setModalMessageText] = useState<React.ReactNode | undefined>(undefined);
  const [openViewModal, setOpenViewModal] = useState<boolean>(false);
  const [viewAlarm, setViewAlarm] = useState<BureauAlarm | null>(null);
  const [recipientContactMap, setRecipientContactMap] = useState<Record<number, string>>({});
  const [selectedIds, setSelectedIds] = useState<Record<number, boolean>>({});
  const [bulkMode, setBulkMode] = useState<'acknowledge' | null>(null);
  const [actionInFlight, setActionInFlight] = useState<string | null>(null);
  const resolverRef = useRef<((value: boolean) => void) | null>(null);

  useEffect(() => {
    const fetchRecipients = async () => {
      try {
        const response = await listRecipients();
        if (response?.success && response.data) {
          const map: Record<number, string> = {};
          response.data.forEach(recipient => {
            if (recipient.recipientId && recipient.contactValue) {
              map[recipient.recipientId] = recipient.contactValue;
            }
          });
          setRecipientContactMap(map);
        }
      } catch (caughtError) {
        console.info('Failed to fetch recipients for contact mapping:', caughtError);
      }
    };

    void fetchRecipients();
  }, []);

  const rows = React.useMemo(
    () => alarms
      .filter(alarm => !alarm.localAck)
      .map((alarm, index) => ({
        id: alarm.bureauAlarmId ?? alarm.id ?? index + 1,
        alarm,
        siteKey: alarm.siteRegistryId ?? alarm.siteGuid ?? alarm.siteIdentifier ?? null,
        siteName: alarm.siteName || alarm.siteIdentifier || UNKNOWN_SITE,
        path: alarm.pathToAlarm || alarm.alarmIdentifier || UNKNOWN_VALUE,
        priority: alarm.priority,
        lastOccurrence: formatDateTime(alarm.lastOccurrenceAt) || UNKNOWN_VALUE,
        alarmState: getAlarmStateText(alarm),
        alarmStateClassName: getAlarmStateClassName(alarm),
        alarmMessage: alarm.alarmMessage || UNKNOWN_VALUE,
      })),
    [alarms]
  );

  const rowsWithIds = React.useMemo(
    () => rows.filter(row => getAlarmId(row.alarm) !== null),
    [rows]
  );

  const allRowsSelected = rowsWithIds.length > 0 && rowsWithIds.every(row => {
    const alarmId = getAlarmId(row.alarm);
    return alarmId !== null && selectedIds[alarmId] === true;
  });

  const selectedAlarms = React.useMemo(
    () => rows
      .map(row => row.alarm)
      .filter(alarm => {
        const alarmId = getAlarmId(alarm);
        return alarmId !== null && selectedIds[alarmId] === true;
      }),
    [rows, selectedIds]
  );

  const anySelected = Object.values(selectedIds || {}).includes(true);
  const hasUnackSelected = selectedAlarms.some(alarm => !alarm.localAck);

  useEffect(() => {
    const validIds = new Set(rows.map(row => getAlarmId(row.alarm)).filter((id): id is number => id !== null));
    setSelectedIds(previous => {
      const next: Record<number, boolean> = {};
      Object.keys(previous).forEach(rawId => {
        const id = Number(rawId);
        if (validIds.has(id) && previous[id]) {
          next[id] = true;
        }
      });
      return next;
    });
  }, [rows]);

  const waitForModalConfirm = (): Promise<boolean> => {
    return new Promise(resolve => {
      resolverRef.current = resolve;
      setOpenModalConfirm(true);
    });
  };

  const handleViewClick = () => {
    setIsExpanded(previous => !previous);
  };

  const handleView = (alarm: BureauAlarm) => {
    setViewAlarm(alarm);
    setOpenViewModal(true);
  };

  const acknowledgeSelected = () => {
    if (!selectedAlarms.length) {
      return;
    }
    setBulkMode('acknowledge');
  };

  const handleToggleSelect = (id: number) => {
    setSelectedIds(previous => ({ ...previous, [id]: !previous[id] }));
  };

  const handleToggleSelectAll = () => {
    if (rowsWithIds.every(row => {
      const alarmId = getAlarmId(row.alarm);
      return alarmId !== null && selectedIds[alarmId];
    })) {
      setSelectedIds({});
      return;
    }

    const next: Record<number, boolean> = {};
    rowsWithIds.forEach(row => {
      const alarmId = getAlarmId(row.alarm);
      if (alarmId !== null) {
        next[alarmId] = true;
      }
    });
    setSelectedIds(next);
  };

  const handleRemoveBulkSelection = (id: number) => {
    setSelectedIds(previous => {
      const copy = { ...previous };
      delete copy[id];
      return copy;
    });
  };

  const handleBulkCopy = async () => {
    if (!selectedAlarms.length) {
      return;
    }

    try {
      const blocks = selectedAlarms.map(alarm => {
        const acknowledgedByName = getAcknowledgedByName(alarm);
        const sections = getAlarmSections(alarm, acknowledgedByName);
        return {
          text: alarmSectionsToText(sections),
          html: alarmSectionsToHtml(sections),
        };
      });

      const finalText = blocks.map(block => block.text).join('\n\n---\n\n');
      const finalHtml = blocks.map(block => block.html).join('<hr/>');

      if (navigator.clipboard?.write) {
        const blobPlain = new Blob([finalText], { type: 'text/plain' });
        const blobHtml = new Blob([finalHtml], { type: 'text/html' });
        const item = new ClipboardItem({ 'text/plain': blobPlain, 'text/html': blobHtml });
        await navigator.clipboard.write([item]);
      } else if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(finalText);
      }

      setSuccessMessage(`Copied ${selectedAlarms.length} alarm(s) to clipboard`);
      setTimeout(() => setSuccessMessage(null), TOAST_SUCCESS_MS);
    } catch {
      setErrorMessage('Failed to copy selected alarms');
      setTimeout(() => setErrorMessage(null), TOAST_ERROR_MS);
    }
  };

  const clearSelectedAlarmIds = (alarmIds: number[]) => {
    if (alarmIds.length === 0) {
      return;
    }
    setSelectedIds(previous => {
      const next = { ...previous };
      alarmIds.forEach(id => {
        delete next[id];
      });
      return next;
    });
  };

  const resolveAcknowledgedByUser = async (): Promise<{ acknowledgedBy: string; userName: string } | null> => {
    const profileResponse = await getProfile();
    if (!profileResponse?.success || !profileResponse.data?.id) {
      setErrorMessage('Unable to retrieve user profile');
      setTimeout(() => setErrorMessage(null), TOAST_ERROR_MS);
      return null;
    }

    const acknowledgedBy = profileResponse.data.id;
    const fullName = [profileResponse.data.firstName, profileResponse.data.lastName].filter(Boolean).join(' ').trim();
    const userName = ([
      fullName,
      profileResponse.data.email,
      userProfile?.preferred_username,
      userProfile?.email,
    ].find(value => typeof value === 'string' && value.length > 0) as string) ?? 'Unknown User';

    return { acknowledgedBy, userName };
  };

  const acknowledgeOneAlarm = async (
    alarm: BureauAlarm,
    acknowledgedBy: string,
    userName: string
  ): Promise<{ alarmId: number | null; success: boolean; label: string }> => {
    const alarmId = getAlarmId(alarm);
    const label = alarm.siteName || alarm.siteIdentifier || (alarmId ? String(alarmId) : 'Unknown');
    if (!alarmId) {
      return { alarmId: null, success: false, label };
    }

    const response = await localAcknowledge(alarmId, {
      acknowledgedBy,
      userName,
    });

    return { alarmId, success: !!response?.success, label };
  };

  const publishAcknowledgementResult = (alarmsToAcknowledge: BureauAlarm[], acknowledgedIds: number[], failedCount: number) => {
    if (acknowledgedIds.length > 0) {
      alarmAcknowledgedEvent.emit({
        source: 'ui',
        eventType: 'AlarmAcknowledged',
        acknowledgedAlarmIds: acknowledgedIds,
      });
      refresh();
      clearSelectedAlarmIds(acknowledgedIds);
    }

    if (acknowledgedIds.length === 1) {
      const label = alarmsToAcknowledge[0].siteName || alarmsToAcknowledge[0].siteIdentifier || String(acknowledgedIds[0]);
      setSuccessMessage(`${label} acknowledged successfully`);
      setTimeout(() => setSuccessMessage(null), TOAST_SUCCESS_MS);
    } else if (acknowledgedIds.length > 1) {
      setSuccessMessage(`${acknowledgedIds.length} alarms acknowledged successfully`);
      setTimeout(() => setSuccessMessage(null), TOAST_SUCCESS_MS);
    }

    if (failedCount > 0 && acknowledgedIds.length === 0) {
      setErrorMessage('Failed to acknowledge selected alarms');
      setTimeout(() => setErrorMessage(null), TOAST_ERROR_MS);
    } else if (failedCount > 0) {
      setErrorMessage(`${failedCount} alarms failed to acknowledge`);
      setTimeout(() => setErrorMessage(null), TOAST_ERROR_MS);
    }
  };

  const acknowledgeAlarms = async (alarmsToAcknowledge: BureauAlarm[]) => {
    if (alarmsToAcknowledge.length === 0) {
      return;
    }

    const firstAlarmId = getAlarmId(alarmsToAcknowledge[0]);
    setAcknowledgingId(firstAlarmId ?? -1);
    setSuccessMessage(null);
    setErrorMessage(null);

    try {
      const user = await resolveAcknowledgedByUser();
      if (!user) {
        return;
      }

      const acknowledgedIds: number[] = [];
      let failedCount = 0;

      for (const alarm of alarmsToAcknowledge) {
        const result = await acknowledgeOneAlarm(alarm, user.acknowledgedBy, user.userName);
        if (result.alarmId !== null) {
          setActionInFlight(String(result.alarmId));
        }
        if (result.success && result.alarmId !== null) {
          acknowledgedIds.push(result.alarmId);
        } else {
          failedCount += 1;
        }
      }

      publishAcknowledgementResult(alarmsToAcknowledge, acknowledgedIds, failedCount);
    } catch (caughtError) {
      setErrorMessage(caughtError instanceof Error ? caughtError.message : 'Failed to acknowledge alarm');
      setTimeout(() => setErrorMessage(null), TOAST_ERROR_MS);
    } finally {
      setActionInFlight(null);
      setAcknowledgingId(null);
    }
  };

  const handleCopy = async (alarm: BureauAlarm) => {
    try {
      const acknowledgedByName = getAcknowledgedByName(alarm);
      const sections = getAlarmSections(alarm, acknowledgedByName);
      const body = alarmSectionsToText(sections);
      const html = alarmSectionsToHtml(sections);

      try {
        const nav = navigator as Navigator & {
          clipboard?: {
            write?: (items: unknown[]) => Promise<void>;
            writeText?: (text: string) => Promise<void>;
          };
        };
        const globalObj = globalThis as typeof globalThis & {
          ClipboardItem?: new (items: Record<string, Blob>) => unknown;
        };

        if (nav.clipboard && typeof nav.clipboard.write === 'function' && globalObj.ClipboardItem) {
          const blobPlain = new Blob([body], { type: 'text/plain' });
          const blobHtml = new Blob([html], { type: 'text/html' });
          const item = new globalObj.ClipboardItem({ 'text/plain': blobPlain, 'text/html': blobHtml });
          await nav.clipboard.write([item]);
        } else if (nav.clipboard && typeof nav.clipboard.writeText === 'function') {
          await nav.clipboard.writeText(body);
        } else {
          throw new Error('Clipboard API unavailable');
        }
      } catch {
        const nav = navigator as Navigator & { clipboard?: { writeText?: (text: string) => Promise<void> } };
        if (nav.clipboard && typeof nav.clipboard.writeText === 'function') {
          await nav.clipboard.writeText(body);
        } else {
          throw new Error('Clipboard API unavailable');
        }
      }

      setSuccessMessage('Copied alarm details to clipboard');
      setTimeout(() => setSuccessMessage(null), TOAST_SUCCESS_MS);
    } catch {
      setErrorMessage('Failed to copy alarm details');
      setTimeout(() => setErrorMessage(null), TOAST_ERROR_MS);
    }
  };

  const handleEmail = async (alarm: BureauAlarm) => {
    try {
      const rawRecipientId = getVariant<number | string>(alarm, ['bureauRecipientId']);
      const recipientId = typeof rawRecipientId === 'string' ? Number(rawRecipientId) : (rawRecipientId ?? null);
      if (!recipientId) {
        setErrorMessage('No email recipient configured for this alarm');
        setTimeout(() => setErrorMessage(null), TOAST_ERROR_MS);
        return;
      }

      const response = await getRecipient(recipientId);
      if (!response?.success || !response.data) {
        setErrorMessage('Failed to lookup recipient');
        setTimeout(() => setErrorMessage(null), TOAST_ERROR_MS);
        return;
      }

      const recipient = response.data;
      if (!recipient.isEmailRecipient) {
        setErrorMessage(`This recipient (${recipient.displayName || recipientId}) is configured for ${recipient.contactType || 'bureau'} notifications, not email. Please add an email recipient to the distribution group.`);
        setTimeout(() => setErrorMessage(null), TOAST_LONG_MS);
        return;
      }

      const email = (recipient.contactValue || '').trim();
      if (!email?.includes('@')) {
        setErrorMessage('Recipient does not have a valid email address');
        setTimeout(() => setErrorMessage(null), TOAST_ERROR_MS);
        return;
      }

      const acknowledgedByName = getAcknowledgedByName(alarm);
      const alarmLabel = alarm.siteName || alarm.siteIdentifier || '';
      const alarmIdentifierSuffix = alarm.alarmIdentifier ? ` - ${alarm.alarmIdentifier}` : '';
      const subject = `[Alarm] ${alarmLabel}${alarmIdentifierSuffix}`.trim();
      const sections = getAlarmSections(alarm, acknowledgedByName);
      const body = alarmSectionsToText(sections);
      const mailto = `mailto:${email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;

      const link = document.createElement('a');
      link.href = mailto;
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      link.remove();

      setSuccessMessage(`Opening email to ${email}`);
      setTimeout(() => setSuccessMessage(null), TOAST_SUCCESS_MS);
    } catch (caughtError) {
      console.info('Error in ultra critical email action:', caughtError);
      setErrorMessage('Failed to open email compose');
      setTimeout(() => setErrorMessage(null), TOAST_ERROR_MS);
    }
  };

  const handleAcknowledge = async (alarm: BureauAlarm) => {
    if (!alarm.bureauAlarmId && !alarm.id) {
      setErrorMessage('Alarm ID is missing');
      return;
    }

    const alarmId = alarm.bureauAlarmId || alarm.id;
    if (!alarmId) {
      setErrorMessage('Alarm ID is missing');
      return;
    }

    const ackAt = getVariant<string | number>(alarm, []);
    const modalContent = (
      <div>
        <div className="form-entry mb-2">
          <div className="form-entry__heading">
            <h3 className="heading__title--building">
              {alarm.siteName || alarm.siteIdentifier || `Alarm ${alarmId}`}
            </h3>
          </div>
          <div className="form-row">
            <span className="form-row__label">Site</span>
            <div className="form-row__info">{alarm.siteName || alarm.siteIdentifier || 'Unknown'}</div>
          </div>
          <div className="form-row">
            <span className="form-row__label">Recipient</span>
            <div className="form-row__info">{formatRecipientName(alarm.bureauRecipientName || alarm.bureauName)}</div>
          </div>
          <div className="form-row">
            <span className="form-row__label">Path</span>
            <div className="form-row__info">{alarm.pathToAlarm || alarm.path || alarm.alarmIdentifier || '-'}</div>
          </div>
          <div className="form-row">
            <span className="form-row__label">State</span>
            <div className="form-row__info">{alarm.bureauAlarmState || 'Unknown'}</div>
          </div>
          <div className="form-row">
            <span className="form-row__label">Last Occurrence</span>
            <div className="form-row__info">{formatDateTime(alarm.lastOccurrenceAt)}</div>
          </div>
          <div className="form-row">
            <span className="form-row__label">Status</span>
            <div className="form-row__info">{alarm.localAck ? 'Already Acknowledged' : 'Not Acknowledged'}</div>
          </div>
          {alarm.localAck && getAcknowledgedByName(alarm) && (
            <div className="form-row">
              <span className="form-row__label">Acknowledged By</span>
              <div className="form-row__info">{getAcknowledgedByName(alarm)}</div>
            </div>
          )}
          {alarm.localAck && ackAt != null && ackAt !== '' && (
            <div className="form-row">
              <span className="form-row__label">Acknowledged At</span>
              <div className="form-row__info">{formatDateTime(String(ackAt))}</div>
            </div>
          )}
        </div>
      </div>
    );

    setModalMessageText(modalContent);
    setModalHeadingText('Acknowledge Alarm');

    const confirmed = await waitForModalConfirm();
    if (!confirmed) {
      return;
    }

    await acknowledgeAlarms([alarm]);
  };

  const handleBulkAcknowledge = async (alarmsToAcknowledge: BureauAlarm[]) => {
    const unacknowledged = alarmsToAcknowledge.filter(alarm => !alarm.localAck);
    if (!unacknowledged.length) {
      setErrorMessage('No unacknowledged alarms selected');
      setTimeout(() => setErrorMessage(null), TOAST_ERROR_MS);
      return;
    }

    await acknowledgeAlarms(unacknowledged);
    setBulkMode(null);
    setSelectedIds({});
  };

  let buttonText = 'View Ultra Critical Alarms';
  if (isExpanded) {
    buttonText = 'Hide Ultra Critical Alarms';
  }

  const viewRecipient = (() => {
    if (!viewAlarm) {
      return '—';
    }

    return formatRecipientName(viewAlarm.bureauRecipientName || viewAlarm.bureauName || viewAlarm.bureauEndpoint || '—');
  })();

  const viewContact = (() => {
    if (!viewAlarm) {
      return '—';
    }

    const rawId = getVariant<number | string>(viewAlarm, ['bureauRecipientId']);
    const recipientId = typeof rawId === 'string' ? Number(rawId) : rawId;
    if (!recipientId) {
      return '—';
    }

    return recipientContactMap[recipientId] ?? '—';
  })();

  const viewAckAt = (() => {
    if (!viewAlarm) {
      return undefined;
    }

    const raw = getVariant<string | number>(viewAlarm, ['localAckAt', 'LocalAckAt', 'acknowledgedAt']);
    if (typeof raw === 'number') {
      return String(raw);
    }

    return raw;
  })();

  if (loading && !isExpanded) {
    return null;
  }

  if (!loading && !error && rows.length === 0) {
    return null;
  }

  return (
    <section className="ultra-critical-panel" role="status" aria-live="polite">
      <Modal
        isOpen={openModalConfirm}
        title={modalHeadingText || 'Confirm Action'}
        content={<ModalContent heading={modalHeadingText} message={modalMessageText} />}
        onConfirm={() => {
          resolverRef.current?.(true);
          setOpenModalConfirm(false);
        }}
        onCancel={() => {
          resolverRef.current?.(false);
          setOpenModalConfirm(false);
        }}
      />
      <Modal
        isOpen={openViewModal}
        title={viewAlarm ? `Alarm ${viewAlarm.bureauAlarmId || viewAlarm.id || ''}` : 'Alarm Details'}
        content={(
          <div>
            {viewAlarm ? (
              <div className="modal__body">
                <div><strong>Site Name:</strong> {viewAlarm.siteName || viewAlarm.siteIdentifier || '—'}</div>
                <div><strong>Site Identifier:</strong> {viewAlarm.siteIdentifier || '—'}</div>
                <div><strong>Recipient:</strong> {viewRecipient}</div>
                <div><strong>Contact:</strong> {viewContact}</div>
                <div><strong>Path:</strong> {viewAlarm.pathToAlarm || viewAlarm.path || viewAlarm.alarmIdentifier || '—'}</div>
                <div><strong>Priority:</strong> {viewAlarm.priority ?? 'Not Set'}</div>
                <div><strong>Last Occurrence:</strong> {formatDateTime(viewAlarm.lastOccurrenceAt)}</div>
                <div><strong>Acknowledged:</strong> {viewAlarm.localAck ? 'Yes' : 'No'}</div>
                {viewAlarm.localAck && getAcknowledgedByName(viewAlarm) && (
                  <div><strong>Acknowledged By:</strong> {getAcknowledgedByName(viewAlarm)}</div>
                )}
                {viewAlarm.localAck && viewAckAt && (
                  <div><strong>Acknowledged At:</strong> {formatDateTime(viewAckAt)}</div>
                )}
                <div><strong>Source Alarm State:</strong> {viewAlarm.sourceAlarmState ?? '—'}</div>
                <div><strong>Alarm Message:</strong> {viewAlarm.alarmMessage ?? '—'}</div>
              </div>
            ) : (
              <div>No data</div>
            )}
          </div>
        )}
        onConfirm={() => setOpenViewModal(false)}
        onCancel={() => setOpenViewModal(false)}
      />
      <Modal
        isOpen={bulkMode === 'acknowledge' && selectedAlarms.length > 0}
        title={`Acknowledge ${selectedAlarms.length} Alarm(s)`}
        content={(
          <div>
            <BureauAcknowledgeForm
              selectedAlarms={selectedAlarms}
              onAcknowledge={async alarmsToAcknowledge => {
                await handleBulkAcknowledge(alarmsToAcknowledge);
                setBulkMode(null);
              }}
              actionInFlight={actionInFlight}
              onRemoveSelected={handleRemoveBulkSelection}
              onClose={() => {
                setBulkMode(null);
                setSelectedIds({});
              }}
              isInsideModal={true}
            />
          </div>
        )}
        onConfirm={async () => {
          await handleBulkAcknowledge(selectedAlarms);
          setBulkMode(null);
        }}
        onCancel={() => {
          setBulkMode(null);
          setSelectedIds({});
        }}
      />
      {successMessage && <StatusMessage kind="success">{successMessage}</StatusMessage>}
      {errorMessage && <StatusMessage kind="error">{errorMessage}</StatusMessage>}
      <BulkActionToolbar visible={anySelected}>
        {hasUnackSelected && (
          <button type="button" className="btn btn__toolbar--approve" onClick={acknowledgeSelected} disabled={!anySelected || acknowledgingId !== null}>
            Acknowledge Selected
          </button>
        )}
        <button type="button" className="btn btn__toolbar--copy" onClick={() => { void handleBulkCopy(); }} disabled={!anySelected || acknowledgingId !== null}>
          Copy Selected
        </button>
        <button type="button" className="btn btn__toolbar--cancel" onClick={() => { setSelectedIds({}); }} disabled={!anySelected || acknowledgingId !== null}>
          Cancel
        </button>
      </BulkActionToolbar>
      <div className="ultra-critical-banner">
        <div className="ultra-critical-banner__alert">
          <div className="ultra-critical-banner__title">Ultra Critical Alarms</div>
          <div className="ultra-critical-banner__subtitle">Immediate attention required</div>
        </div>

        <div className="ultra-critical-banner__metric">
          <span className="ultra-critical-banner__metric-value">{rows.length}</span>
          <span className="ultra-critical-banner__metric-label">Active Now</span>
        </div>

        <div className="ultra-critical-banner__metric">
          <span className="ultra-critical-banner__metric-value">{newLast15Minutes}</span>
          <span className="ultra-critical-banner__metric-label">New (Last 15m)</span>
        </div>

        <div className="ultra-critical-banner__metric">
          <span className="ultra-critical-banner__metric-value">{sitesAffected}</span>
          <span className="ultra-critical-banner__metric-label">Sites Affected</span>
        </div>

        <div className="ultra-critical-banner__action">
          <button type="button" className="btn btn__error" onClick={handleViewClick}>
            {buttonText}
          </button>
        </div>
      </div>

      {loading && isExpanded && (
        <div className="ultra-critical-table-wrap">
          <LoadingSpinner message="Loading ultra critical alarms..." />
        </div>
      )}

      {!loading && error && isExpanded && (
        <div className="ultra-critical-table-wrap" role="alert">
          <p className="text-danger mb-2">{error}</p>
          <button type="button" className="btn btn__error" onClick={refresh}>
            Retry
          </button>
        </div>
      )}

      {!loading && !error && isExpanded && (
        <div className="ultra-critical-table-wrap">
          {rows.length === 0 ? (
            <p className="mb-0 mt-0">No ultra critical alarms found.</p>
          ) : (
          <table className="ultra-critical-table" aria-label="Ultra critical alarms">
            <thead>
              <tr>
                <th className="ultra-critical-table__checkbox">
                  <input
                    type="checkbox"
                    aria-label="Select all"
                    checked={allRowsSelected}
                    disabled={rowsWithIds.length === 0 || acknowledgingId !== null}
                    onChange={handleToggleSelectAll}
                  />
                </th>
                <th>Site Name</th>
                <th>Path</th>
                <th>Priority</th>
                <th>Last Occurrence</th>
                <th>Alarm State</th>
                <th>Alarm Message</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(row => {
                const actions = [
                  ...(!row.alarm.localAck ? [{
                    label: 'Acknowledge',
                    onClick: () => handleAcknowledge(row.alarm),
                    className: 'actions__item actions__item--acknowledge',
                    disabled: acknowledgingId !== null,
                  }] : []),
                  {
                    label: 'View',
                    onClick: () => handleView(row.alarm),
                    className: 'actions__item actions__item--view',
                  },
                  {
                    label: 'Email',
                    onClick: () => handleEmail(row.alarm),
                    className: 'actions__item actions__item--email',
                  },
                  {
                    label: 'Copy',
                    onClick: () => handleCopy(row.alarm),
                    className: 'actions__item actions__item--copy',
                  }
                ];

                return (
                <tr key={row.id}>
                  <td>
                    <input
                      type="checkbox"
                      aria-label={`Select row ${row.id}`}
                      checked={(() => {
                        const alarmId = getAlarmId(row.alarm);
                        return alarmId !== null && selectedIds[alarmId] === true;
                      })()}
                      disabled={getAlarmId(row.alarm) === null || acknowledgingId !== null}
                      onChange={() => {
                        const alarmId = getAlarmId(row.alarm);
                        if (alarmId !== null) {
                          handleToggleSelect(alarmId);
                        }
                      }}
                    />
                  </td>
                  <td>
                    <div className="ultra-critical-table__site-cell">
                      {row.siteKey ? (
                        <Link to={`/sites/${encodeURIComponent(String(row.siteKey))}`} className="ultra-critical-table__site-chip">
                           {row.siteName}
                        </Link>
                      ) : (
                        <span className="ultra-critical-table__site-chip">
                           {row.siteName}
                        </span>
                      )}
                    </div>
                  </td>
                  <td>{row.path}</td>
                  <td>
                    <div className="ultra-critical-table__priority">{row.priority ?? 'Not Set'}</div>
                  </td>
                  <td>{row.lastOccurrence}</td>
                  <td>
                    <span className={`status ${row.alarmStateClassName}`}>{row.alarmState}</span>
                  </td>
                  <td>{row.alarmMessage}</td>
                  <td>
                    <div className="actions">
                      <div className="actions__list">
                        <ActionList customActions={actions} disabled={acknowledgingId !== null} />
                      </div>
                    </div>
                  </td>
                </tr>
              );})}
            </tbody>
          </table>
          )}
        </div>
      )}
    </section>
  );
};

export default UltraCriticalAlarms;
